import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Copy, RefreshCw, ExternalLink, Check, Plus, Trash2, Send, Eye, EyeOff, Loader2, X } from "lucide-react";
import { PageHeader } from "@/components/shared/PageHeader";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { apiFetch } from "@/lib/api";
import { cn } from "@/lib/utils";

interface Channel {
  id: number;
  display_phone: string;
  name: string | null;
  verified_name: string | null;
  phone_number_id: string;
}

interface WebhookSubscription {
  id: number;
  channel_id: number;
  channel_name: string;
  phone_number_id: string;
  event_type: string;
  target_url: string;
  active: boolean;
  created_at: string;
  updated_at: string;
}

interface DeliveryLog {
  id: number;
  event_type: string;
  request_url: string;
  response_status: number | null;
  latency_ms: number;
  attempt_number: number;
  status: string;
  error_message: string | null;
  delivered_at: string | null;
  created_at: string;
}

interface ApiToken {
  id: number;
  name: string;
  prefix: string;
  raw_token?: string;
  last_used_at: string | null;
  revoked: boolean;
  created_at: string;
}

function ApiKeysTab() {
  const queryClient = useQueryClient();
  const [newTokenName, setNewTokenName] = useState("");
  const [creating, setCreating] = useState(false);
  const [justCreatedToken, setJustCreatedToken] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const { data } = useQuery({
    queryKey: ["api_tokens"],
    queryFn: () => apiFetch<{ api_tokens: ApiToken[] }>("/api/api_tokens"),
  });

  const tokens = data?.api_tokens?.filter((t) => !t.revoked) ?? [];

  const createMutation = useMutation({
    mutationFn: (name: string) =>
      apiFetch<{ api_token: ApiToken }>("/api/api_tokens", {
        method: "POST",
        body: JSON.stringify({ name }),
      }),
    onSuccess: (data) => {
      setJustCreatedToken(data.api_token.raw_token || null);
      setCreating(false);
      setNewTokenName("");
      queryClient.invalidateQueries({ queryKey: ["api_tokens"] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) =>
      apiFetch(`/api/api_tokens/${id}`, { method: "DELETE" }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["api_tokens"] }),
  });

  const copyToken = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="space-y-4">
      {justCreatedToken && (
        <div className="rounded-lg border border-success/40 bg-success/10 p-4">
          <p className="text-sm font-medium text-success">API key created — copy it now, you won't see it again.</p>
          <div className="mt-2 flex items-center gap-2">
            <code className="flex-1 rounded-md border border-border bg-background px-3 py-2 font-mono text-xs break-all">
              {justCreatedToken}
            </code>
            <Button variant="outline" size="sm" onClick={() => copyToken(justCreatedToken)}>
              {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              {copied ? "Copied" : "Copy"}
            </Button>
          </div>
          <Button variant="ghost" size="sm" className="mt-2 text-xs" onClick={() => setJustCreatedToken(null)}>
            <X className="h-3 w-3" />Dismiss
          </Button>
        </div>
      )}

      <div className="rounded-lg border border-border bg-card p-5">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold">API keys</h3>
            <p className="text-xs text-muted-foreground">Use these keys in the Authorization header as Bearer tokens.</p>
          </div>
          {!creating && (
            <Button size="sm" onClick={() => setCreating(true)}>
              <Plus className="h-4 w-4" />New key
            </Button>
          )}
        </div>

        {creating && (
          <div className="mt-3 flex items-center gap-2">
            <Input
              className="max-w-xs text-sm"
              placeholder="Key name (e.g. Production)"
              value={newTokenName}
              onChange={(e) => setNewTokenName(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && newTokenName.trim()) createMutation.mutate(newTokenName.trim()); }}
            />
            <Button size="sm" onClick={() => createMutation.mutate(newTokenName.trim() || "Default")}
              disabled={createMutation.isPending}>
              {createMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create"}
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setCreating(false)}>Cancel</Button>
          </div>
        )}

        {tokens.length === 0 && !creating ? (
          <p className="mt-4 text-xs text-muted-foreground">No API keys yet. Create one to start using the API.</p>
        ) : (
          <div className="mt-4 divide-y divide-border">
            {tokens.map((token) => (
              <div key={token.id} className="flex items-center justify-between py-3">
                <div>
                  <p className="text-sm font-medium">{token.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {token.prefix}••••••••
                    {token.last_used_at ? ` · Last used ${new Date(token.last_used_at).toLocaleDateString()}` : " · Never used"}
                  </p>
                </div>
                <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:text-destructive"
                  onClick={() => { if (confirm("Revoke this API key? It will stop working immediately.")) deleteMutation.mutate(token.id); }}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="rounded-lg border border-border bg-card p-5">
        <h3 className="text-sm font-semibold">Quick start</h3>
        <pre className="mt-3 overflow-x-auto rounded-md bg-foreground p-4 text-[11px] leading-relaxed text-background">
{`curl -X POST ${window.location.origin}/api/v1/messages \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "phone_number_id": "YOUR_PHONE_NUMBER_ID",
    "to": "51992606275",
    "type": "text",
    "body": "Hello from the API!"
  }'`}
        </pre>
      </div>
    </div>
  );
}

const EVENT_TYPES = [
  { key: "inbound_message", label: "Inbound messages", desc: "Receive POST callbacks when someone sends a message to your number" },
  { key: "message_status", label: "Message statuses", desc: "Delivery status updates for outbound messages (sent, delivered, read, failed)" },
  { key: "template_update", label: "Template updates", desc: "Template approval, rejection, and pause status changes" },
  { key: "account_update", label: "Account updates", desc: "Phone number quality rating and messaging tier changes" },
  { key: "quality_update", label: "Quality updates", desc: "Phone number quality rating and messaging limit changes" },
  { key: "name_update", label: "Name updates", desc: "Verified name approval or rejection for your phone number" },
  { key: "account_review", label: "Account review", desc: "WhatsApp Business Account review decisions (approved/rejected)" },
  { key: "security", label: "Security events", desc: "Security changes like PIN updates and two-step verification status" },
  { key: "user_id_update", label: "User ID updates", desc: "BSUID changes when a user updates their phone number or WhatsApp username" },
];

function ChannelSigningSecret({ channelId, subscriptions }: { channelId: string; subscriptions: WebhookSubscription[] }) {
  const [showSecret, setShowSecret] = useState(false);
  const [revealedSecret, setRevealedSecret] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const queryClient = useQueryClient();

  const firstSub = subscriptions[0];

  const rotateMutation = useMutation({
    mutationFn: () =>
      apiFetch<{ signing_secret: string }>(`/api/webhook_subscriptions/${firstSub?.id}/rotate_secret`, { method: "POST" }),
    onSuccess: (data) => {
      setRevealedSecret(data.signing_secret);
      setShowSecret(true);
      queryClient.invalidateQueries({ queryKey: ["webhook_subscriptions"] });
    },
  });

  if (!firstSub) return null;

  return (
    <div className="rounded-lg border border-border bg-card p-5">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold">Webhook signing secret</h3>
          <p className="text-xs text-muted-foreground">All webhooks on this number share the same signing secret for HMAC-SHA256 verification.</p>
        </div>
      </div>
      <div className="mt-3 flex items-center gap-2">
        <code className="rounded border border-border bg-muted/40 px-2 py-0.5 font-mono text-xs">
          {showSecret && revealedSecret ? revealedSecret : "whsec_••••••••••••••••"}
        </code>
        <Button variant="ghost" size="icon" className="h-6 w-6" onClick={async () => {
          if (showSecret) { setShowSecret(false); return; }
          if (!revealedSecret) {
            const data = await apiFetch<{ signing_secret: string }>(`/api/webhook_subscriptions/${firstSub.id}/reveal_secret`);
            setRevealedSecret(data.signing_secret);
          }
          setShowSecret(true);
        }}>
          {showSecret ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
        </Button>
        {showSecret && revealedSecret && (
          <Button variant="ghost" size="sm" className="h-6 text-xs" onClick={() => {
            navigator.clipboard.writeText(revealedSecret);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
          }}>
            {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
            {copied ? "Copied" : "Copy"}
          </Button>
        )}
        <Button variant="ghost" size="sm" className="h-6 text-xs" onClick={() => {
          if (confirm("Rotate signing secret? This will affect ALL webhooks on this number. The old secret will stop working immediately.")) rotateMutation.mutate();
        }}>
          <RefreshCw className="h-3 w-3" />Rotate
        </Button>
      </div>
    </div>
  );
}

function WebhookCard({
  eventType,
  subscription,
  channelId,
  onMutate,
}: {
  eventType: typeof EVENT_TYPES[number];
  subscription: WebhookSubscription | undefined;
  channelId: string;
  onMutate: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [url, setUrl] = useState(subscription?.target_url ?? "");

  const createMutation = useMutation({
    mutationFn: () =>
      apiFetch<{ webhook_subscription: WebhookSubscription }>("/api/webhook_subscriptions", {
        method: "POST",
        body: JSON.stringify({ channel_id: Number(channelId), event_type: eventType.key, target_url: url }),
      }),
    onSuccess: () => {
      setEditing(false);
      onMutate();
    },
  });

  const updateMutation = useMutation({
    mutationFn: (params: Record<string, unknown>) =>
      apiFetch(`/api/webhook_subscriptions/${subscription!.id}`, {
        method: "PATCH",
        body: JSON.stringify(params),
      }),
    onSuccess: () => { setEditing(false); onMutate(); },
  });

  const deleteMutation = useMutation({
    mutationFn: () =>
      apiFetch(`/api/webhook_subscriptions/${subscription!.id}`, { method: "DELETE" }),
    onSuccess: () => { setUrl(""); onMutate(); },
  });

  const testMutation = useMutation({
    mutationFn: () =>
      apiFetch(`/api/webhook_subscriptions/${subscription!.id}/test`, { method: "POST" }),
  });

  const isActive = subscription?.active;
  const statusLabel = subscription ? (isActive ? "Active" : "Paused") : "Not configured";

  return (
    <div className="rounded-lg border border-border bg-card p-5">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold">{eventType.label}</h3>
          <p className="text-xs text-muted-foreground">{eventType.desc}</p>
        </div>
        <span className={cn("inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs",
          isActive ? "border-success/30 bg-success/10 text-success"
            : subscription ? "border-warning/30 bg-warning/10 text-warning"
            : "border-border bg-muted text-muted-foreground")}>
          {isActive && <Check className="h-3 w-3" />}
          {statusLabel}
        </span>
      </div>

      <div className="mt-3 flex items-center gap-2">
        <Input
          className="flex-1 font-mono text-xs"
          value={editing ? url : (subscription?.target_url || "")}
          onChange={(e) => { setUrl(e.target.value); if (!editing) setEditing(true); }}
          placeholder="https://your-domain.com/webhook"
          readOnly={!!subscription && !editing}
        />
        {!subscription ? (
          <Button
            variant="outline"
            size="sm"
            disabled={!url.trim() || createMutation.isPending}
            onClick={() => createMutation.mutate()}
          >
            {createMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            Subscribe
          </Button>
        ) : editing ? (
          <>
            <Button variant="outline" size="sm" onClick={() => { setEditing(false); setUrl(subscription.target_url); }}>
              Cancel
            </Button>
            <Button
              size="sm"
              disabled={!url.trim() || updateMutation.isPending}
              onClick={() => updateMutation.mutate({ target_url: url })}
            >
              Save
            </Button>
          </>
        ) : (
          <>
            <Button
              variant="outline"
              size="sm"
              disabled={testMutation.isPending}
              onClick={() => testMutation.mutate()}
            >
              {testMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              Test
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => updateMutation.mutate({ active: !isActive })}
            >
              {isActive ? "Pause" : "Resume"}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => { setEditing(true); setUrl(subscription.target_url); }}
            >
              Edit
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => { if (confirm("Delete this webhook subscription?")) deleteMutation.mutate(); }}
            >
              <Trash2 className="h-4 w-4 text-destructive" />
            </Button>
          </>
        )}
      </div>

      {(createMutation.isError || updateMutation.isError) && (
        <p className="mt-2 text-xs text-destructive">
          {(createMutation.error || updateMutation.error)?.message}
        </p>
      )}

      {testMutation.isSuccess && (
        <p className="mt-2 text-xs text-success">Test event queued successfully.</p>
      )}

    </div>
  );
}

function DeliveryLogsTab({ channelId }: { channelId: string }) {
  const [selectedSubId, setSelectedSubId] = useState<string>("");

  const { data: subsData } = useQuery({
    queryKey: ["webhook_subscriptions", channelId],
    queryFn: () => apiFetch<{ webhook_subscriptions: WebhookSubscription[] }>(
      `/api/webhook_subscriptions${channelId ? `?channel_id=${channelId}` : ""}`
    ),
  });

  const subs = subsData?.webhook_subscriptions ?? [];
  const subId = selectedSubId || (subs[0]?.id ? String(subs[0].id) : "");

  const { data: logsData } = useQuery({
    queryKey: ["delivery_logs", subId],
    queryFn: () => apiFetch<{ delivery_logs: DeliveryLog[] }>(`/api/webhook_subscriptions/${subId}/delivery_logs`),
    enabled: !!subId,
    refetchInterval: 10000,
  });

  const logs = logsData?.delivery_logs ?? [];

  if (subs.length === 0) {
    return (
      <div className="rounded-lg border border-muted bg-muted/30 px-4 py-8 text-center text-sm text-muted-foreground">
        No webhook subscriptions yet. Create one in the Webhooks tab to see delivery logs.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <label className="mb-1 block text-xs font-medium text-muted-foreground">Subscription</label>
        <select
          value={subId}
          onChange={(e) => setSelectedSubId(e.target.value)}
          className="w-full max-w-sm rounded-md border border-border bg-background px-3 py-2 text-sm"
        >
          {subs.map((s) => (
            <option key={s.id} value={s.id}>{s.event_type} — {s.target_url}</option>
          ))}
        </select>
      </div>

      <div className="overflow-hidden rounded-lg border border-border bg-card">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="px-4 py-3 text-left font-medium">Timestamp</th>
              <th className="px-4 py-3 text-left font-medium">Event</th>
              <th className="px-4 py-3 text-left font-medium">Status</th>
              <th className="px-4 py-3 text-left font-medium">Response</th>
              <th className="px-4 py-3 text-left font-medium">Latency</th>
              <th className="px-4 py-3 text-left font-medium">Attempt</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {logs.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-xs text-muted-foreground">No delivery logs yet.</td>
              </tr>
            ) : logs.map((log) => (
              <tr key={log.id} className="hover:bg-muted/40">
                <td className="whitespace-nowrap px-4 py-3 text-xs text-muted-foreground">{new Date(log.created_at).toLocaleString()}</td>
                <td className="px-4 py-3 text-xs">{log.event_type}</td>
                <td className="px-4 py-3">
                  <span className={cn("rounded px-1.5 py-0.5 font-mono text-xs",
                    log.status === "success" ? "bg-success/15 text-success" : "bg-destructive/15 text-destructive")}>
                    {log.status}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <span className={cn("rounded px-1.5 py-0.5 font-mono text-xs",
                    log.response_status && log.response_status < 300 ? "bg-success/15 text-success" : "bg-destructive/15 text-destructive")}>
                    {log.response_status || "—"}
                  </span>
                </td>
                <td className="px-4 py-3 text-xs">{log.latency_ms}ms</td>
                <td className="px-4 py-3 text-xs">{log.attempt_number}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function Integration() {
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<"docs" | "keys" | "webhooks" | "logs">("webhooks");
  const [channelId, setChannelId] = useState("");

  const { data: channelsData } = useQuery({
    queryKey: ["channels"],
    queryFn: () => apiFetch<{ channels: Channel[] }>("/api/channels"),
  });

  const channels = channelsData?.channels ?? [];

  const selectedChannelId = channelId || (channels[0]?.id ? String(channels[0].id) : "");

  const { data: subsData } = useQuery({
    queryKey: ["webhook_subscriptions", selectedChannelId],
    queryFn: () => apiFetch<{ webhook_subscriptions: WebhookSubscription[] }>(
      `/api/webhook_subscriptions?channel_id=${selectedChannelId}`
    ),
    enabled: !!selectedChannelId,
  });

  const subscriptions = subsData?.webhook_subscriptions ?? [];
  const subsByType = Object.fromEntries(subscriptions.map((s) => [s.event_type, s]));

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["webhook_subscriptions"] });

  return (
    <div className="space-y-6">
      <PageHeader title="Integration" description="Connect your backend to InferenceLabs via REST API and webhooks." />

      <div className="flex items-center gap-1 border-b border-border">
        {[
          { id: "docs", label: "Documentation" },
          { id: "keys", label: "API keys" },
          { id: "webhooks", label: "Webhooks" },
          { id: "logs", label: "Delivery logs" },
        ].map((t) => (
          <button key={t.id} onClick={() => setTab(t.id as any)}
            className={cn("border-b-2 px-3 py-2 text-sm font-medium -mb-px",
              tab === t.id ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground")}>
            {t.label}
          </button>
        ))}
      </div>

      {(tab === "webhooks" || tab === "logs") && channels.length > 0 && (
        <div>
          <label className="mb-1 block text-xs font-medium text-muted-foreground">Channel</label>
          <select
            value={selectedChannelId}
            onChange={(e) => setChannelId(e.target.value)}
            className="w-full max-w-sm rounded-md border border-border bg-background px-3 py-2 text-sm"
          >
            {channels.map((ch) => (
              <option key={ch.id} value={ch.id}>{ch.name || ch.verified_name || ch.display_phone}</option>
            ))}
          </select>
        </div>
      )}

      {tab === "keys" && (
        <ApiKeysTab />
      )}

      {tab === "webhooks" && (
        <div className="space-y-4">
          {channels.length === 0 ? (
            <div className="rounded-lg border border-muted bg-muted/30 px-4 py-8 text-center text-sm text-muted-foreground">
              No channels connected yet. Go to Channels and connect a WhatsApp number first.
            </div>
          ) : (
            <>
              <ChannelSigningSecret channelId={selectedChannelId} subscriptions={subscriptions} />
              {EVENT_TYPES.map((et) => (
                <WebhookCard
                  key={et.key}
                  eventType={et}
                  subscription={subsByType[et.key]}
                  channelId={selectedChannelId}
                  onMutate={invalidate}
                />
              ))}
            </>
          )}
        </div>
      )}

      {tab === "logs" && (
        <DeliveryLogsTab channelId={selectedChannelId} />
      )}

      {tab === "docs" && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {[
            { title: "WhatsApp Cloud API", desc: "Send templates, media and conversational messages." },
            { title: "Webhooks reference", desc: "Receive delivery, inbound and template events." },
            { title: "SDKs", desc: "Official clients for Node, Python, Go and PHP." },
            { title: "Rate limits", desc: "Per-channel send rates and burst allowances." },
          ].map((d) => (
            <a key={d.title} href="#" className="group rounded-lg border border-border bg-card p-5 hover:border-primary/40">
              <h3 className="text-sm font-semibold">{d.title}</h3>
              <p className="mt-1 text-xs text-muted-foreground">{d.desc}</p>
              <span className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-primary">Open <ExternalLink className="h-3 w-3" /></span>
            </a>
          ))}
        </div>
      )}
    </div>
  );
}
